/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { call, all, select, put, race, fork, join } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  Asset,
  AssetMediaType,
  FileFormatToFileTypeMap,
  Constants,
  AssetsApi,
  SearchUtils,
  ContentProps,
  ParentalControlSettings,
  HideUnsubscribed,
  Ducks,
  AssetUtils,
  MenuUiArea,
  MenuItem,
  MenuItemAlias,
} from '@wit/vtv-x-foundation-lib';
import AppConfig from '../../../../services/AppConfig';

export enum RelatedRailType {
  GENRE = 'GENRE',
  ACTOR = 'ACTOR',
  DIRECTOR = 'DIRECTOR',
  RELATED_CONTENT = 'RELATED_CONTENT',
}

enum RelatedActorMenuItemName {
  RELATED_BY_ACTOR1 = 'relatedbyactor1',
  RELATED_BY_ACTOR2 = 'relatedbyactor2',
  RELATED_BY_ACTOR3 = 'relatedbyactor3',
}

export interface RelatedRail {
  id: number | string;
  type: RelatedRailType;
  name?: string;
  assets: Asset[];
  menuItem?: MenuItem;
  isRecomm?: boolean;
}

function* getRecommRelatedAssets(asset: Asset, menuItemId?: number): SagaIterator<Asset[]> {
  if (!menuItemId) {
    return [];
  }

  const recommContext: Ducks.Recommendations.RecommContext = {
    menuUiArea: MenuUiArea.RELATED,
    menuItemId,
    relatedAssetId: Ducks.Recommendations.getRelatedAssetId(asset),
  };

  yield put(Ducks.Recommendations.Creators.loadAssets(recommContext));

  yield race([
    Ducks.Recommendations.takeAssets(Ducks.Recommendations.ActionType.SUCCESS, recommContext),
    Ducks.Recommendations.takeAssets(Ducks.Recommendations.ActionType.FAIL, recommContext),
  ]);

  const assets = yield select(Ducks.Recommendations.Selectors.assets, recommContext);

  return AssetUtils.merge(assets);
}

function getActorNameByRelatedItemName(asset: Asset, menuItemName: string): string | undefined {
  const { actors } = asset as ContentProps;

  if (!Array.isArray(actors) || actors.length === 0) {
    return undefined;
  }

  switch (menuItemName) {
    case RelatedActorMenuItemName.RELATED_BY_ACTOR1:
      return actors[0];
    case RelatedActorMenuItemName.RELATED_BY_ACTOR2:
      return actors[1];
    case RelatedActorMenuItemName.RELATED_BY_ACTOR3:
      return actors[2];
    default:
      return undefined;
  }
}

function getDirectorName(asset: Asset) {
  const { directors } = asset as ContentProps;

  if (!Array.isArray(directors) || directors.length === 0) {
    return undefined;
  }

  return directors[0];
}

function* getRecommRelatedRail(asset: Asset, menuItem: MenuItem): SagaIterator<RelatedRail | undefined> {
  let relatedRail: RelatedRail;
  switch (menuItem.alias) {
    case MenuItemAlias.RELATED_BY_ACTOR: {
      relatedRail = {
        id: menuItem.id,
        type: RelatedRailType.ACTOR,
        assets: [],
        isRecomm: true,
        name: getActorNameByRelatedItemName(asset, menuItem.name),
      };
      break;
    }
    case MenuItemAlias.RELATED_BY_DIRECTOR: {
      relatedRail = {
        id: menuItem.id,
        type: RelatedRailType.DIRECTOR,
        assets: [],
        isRecomm: true,
        name: getDirectorName(asset),
      };
      break;
    }
    case MenuItemAlias.RELATED_BY_GENRE: {
      relatedRail = {
        id: menuItem.id,
        type: RelatedRailType.GENRE,
        assets: [],
        isRecomm: true,
        name: asset.metadata?.genre,
      };
      break;
    }
    case MenuItemAlias.RELATED_CONTENT: {
      relatedRail = {
        id: menuItem.id,
        type: RelatedRailType.RELATED_CONTENT,
        assets: [],
        isRecomm: true,
        name: asset.title,
      };
      break;
    }
    default:
      return undefined;
  }

  relatedRail.menuItem = menuItem;

  relatedRail.assets = yield call(getRecommRelatedAssets, asset, menuItem.id);

  return relatedRail;
}

function* getAndMergeRelatedAssets(
  namespace: string,
  ksql: string,
  mediaTypes: AssetMediaType[]
): SagaIterator<Asset[]> {
  const filesFormat: FileFormatToFileTypeMap = yield select(Ducks.Dms.Selectors.getFilesFormat);
  const parentalControlSettings: ParentalControlSettings = yield select(Ducks.Dms.Selectors.getParentalControl);

  const result: Asset[] = yield Ducks.Pagination.callPaginatedApi(
    namespace,
    0,
    Constants.ASSETS_NUMBER_RELATED,
    Constants.ASSETS_NUMBER_RELATED,
    AssetsApi.assetSearch,
    ksql,
    filesFormat,
    parentalControlSettings,
    mediaTypes,
    undefined,
    HideUnsubscribed[AppConfig.env.CHANNELS_UNSUBSCRIBED_VISIBILITY]
  );

  const mergedAssets = AssetUtils.merge(result);

  return mergedAssets;
}

function* getRelatedByGenre(asset: Asset, mediaTypes: AssetMediaType[]): SagaIterator<RelatedRail> {
  const result: RelatedRail = { id: 'genre', type: RelatedRailType.GENRE, assets: [] };

  const ksql = SearchUtils.createGenreFilter(asset);

  if (ksql === '') {
    return result;
  }

  const results: Asset[] = yield call(getAndMergeRelatedAssets, `related_genres_${asset.id}`, ksql, mediaTypes);
  result.assets = results;

  return result;
}

function* getRelatedByActor(asset: Asset, mediaTypes: AssetMediaType[], actorName: string): SagaIterator<Asset[]> {
  const ksql = SearchUtils.createActorFilter(asset, mediaTypes, actorName);

  if (ksql === '') {
    return [];
  }

  return yield call(getAndMergeRelatedAssets, `related_actor_${actorName}_${asset.id}`, ksql, mediaTypes);
}

function* getRelatedByDirector(asset: Asset, mediaTypes: AssetMediaType[]): SagaIterator<RelatedRail> {
  const { directors } = asset as ContentProps;
  const result: RelatedRail = { id: 'director', type: RelatedRailType.DIRECTOR, assets: [] };

  if (!Array.isArray(directors) || directors.length === 0) {
    return result;
  }

  const ksql = SearchUtils.createDirectorFilter(asset, mediaTypes, directors[0]);

  if (ksql === '') {
    return result;
  }

  const results: [] = yield call(getAndMergeRelatedAssets, `related_director_${asset.id}`, ksql, mediaTypes);
  result.assets = results;

  return result;
}

function* getRelatedByActors(asset: Asset, mediaTypes: AssetMediaType[]): SagaIterator<RelatedRail[]> {
  const { actors } = asset as ContentProps;

  if (!Array.isArray(actors) || actors.length === 0) {
    return [];
  }

  const tasks = [];
  const rails: RelatedRail[] = [];

  for (let i = 0; i < Math.min(actors.length, Constants.RELATED_ACTOR_NUMBER_RAIL); i += 1) {
    tasks.push(call(getRelatedByActor, asset, mediaTypes, actors[i]));
    rails.push({ id: `actor-${i}`, type: RelatedRailType.ACTOR, name: actors[i], assets: [] });
  }

  const results: [] = yield all(tasks);

  for (let j = 0; j < results.length; j += 1) {
    rails[j].assets = results[j];
  }

  return rails;
}

function* getRelatedForMedias(asset: Asset): SagaIterator<RelatedRail[]> {
  const mediaTypes = [AssetMediaType.MOVIE, AssetMediaType.EPISODE, AssetMediaType.SERIES, AssetMediaType.FOLDER];

  const [actors, director, genre] = yield all([
    // actors
    call(getRelatedByActors, asset, mediaTypes),
    // directors
    call(getRelatedByDirector, asset, mediaTypes),
    // genres
    call(getRelatedByGenre, asset, mediaTypes),
  ]);

  return [...actors, director, genre];
}

function* getRelatedForEpgs(asset: Asset): SagaIterator {
  const mediaTypes = [AssetMediaType.MOVIE, AssetMediaType.EPG];
  const results = yield call(getRelatedByGenre, asset, mediaTypes);
  return [results];
}

function* getRelatedRecomm(asset: Asset): SagaIterator<RelatedRail[]> {
  const relatedMenuItem: MenuItem | undefined = yield select(Ducks.Menu.Selectors.getMainMenuItem, MenuUiArea.RELATED);

  if (!relatedMenuItem || !relatedMenuItem.children?.length) {
    return [];
  }

  const tasks = [];

  for (let i = 0; i < relatedMenuItem.children.length; i += 1) {
    tasks.push(yield fork(getRecommRelatedRail, asset, relatedMenuItem.children[i]));
  }

  const relatedRails: RelatedRail[] = yield join(tasks);

  return relatedRails.filter((el?: RelatedRail) => el !== undefined);
}

function* getRelatedDefault(asset: Asset): SagaIterator {
  switch (asset.mediaType) {
    case AssetMediaType.EPISODE:
    case AssetMediaType.MOVIE:
    case AssetMediaType.SERIES:
      return yield call(getRelatedForMedias, asset);
    case AssetMediaType.EPG:
      return yield call(getRelatedForEpgs, asset);
    // case AssetMediaType.RECORDING: // TODO: add logic to recording
    default:
      return [];
  }
}

function* getRelated(asset: Asset): SagaIterator {
  const isRecommEnabled: boolean = yield select(Ducks.Recommendations.Selectors.isFeatureEnabled);

  if (isRecommEnabled) {
    return yield call(getRelatedRecomm, asset);
  }

  return yield call(getRelatedDefault, asset);
}

export default {
  getRelated,
};
